import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Sparkles, HeartHandshake, BookOpen, Check } from "lucide-react";
import { BloomHerLogo } from "@/components/BloomHerLogo";

const HERO = "/hub.png";

const features = [
  { icon: Sparkles, title: "Daily Ritual", desc: "A gentle morning check-in for mood, energy, sleep, and stress — self-tracking, never clinical." },
  { icon: HeartHandshake, title: "Faith & Encouragement", desc: "A daily scripture, a word of encouragement, and a quiet space to pray and reflect." },
  { icon: BookOpen, title: "Premium Library", desc: "Trusted education on menopause, hormones, metabolic health, sleep, stress, and GLP-1." },
];

const benefits = [
  "Daily Ritual check-ins with wellness score & trends",
  "Daily KJV scripture, encouragement & prayer list",
  "Full premium content library across all topics",
  "Streaks, milestones & gentle rewards",
  "Guided handoffs to consults & supplements",
];

export default function Landing() {
  const { user } = useAuth();
  const cta = user ? "/dashboard" : "/signup";

  return (
    <div className="min-h-screen bg-cream">
      <header className="sticky top-0 z-50 w-full bg-cream/80 backdrop-blur-xl border-b border-taupe/50">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 flex items-center justify-between h-20">
          <div className="flex items-center gap-2">
            <BloomHerLogo size="text-3xl" />
          </div>
          <div className="flex items-center gap-3">
            <Link to="/login" data-testid="nav-login-link" className="hidden sm:inline-flex rounded-full px-5 py-2.5 text-sm font-medium text-[#6E5862] hover:text-wine transition-colors">
              Sign in
            </Link>
            <Link to={cta} data-testid="nav-join-link" className="inline-flex items-center justify-center rounded-full bg-wine px-6 py-2.5 text-sm font-medium text-white transition-all hover:bg-wine-dark hover:shadow-md">
              {user ? "My Dashboard" : "Become a Member"}
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden" style={{ background: "radial-gradient(120% 100% at 50% 0%, #FBE7EF 0%, #F8EAF1 35%, #FBF1F4 80%)" }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8 pt-12 pb-16 lg:pt-16">
          <div className="overflow-hidden rounded-[2rem] sm:rounded-[2.5rem] shadow-[0_20px_50px_rgba(58,18,48,0.15)] border border-white/60 animate-fade-up">
            <img src={HERO} alt="BloomHer Wellness Hub — your daily companion for whole-body wellness, whole-heart healing" className="w-full h-auto block" />
          </div>
          <div className="mt-10 flex flex-col items-center text-center animate-fade-up" style={{ animationDelay: "0.1s" }}>
            <p className="text-lg font-sans text-[#6E5862] leading-relaxed max-w-xl">
              Your gated daily companion — wellness check-ins, faith-centered encouragement,
              ShapeUp weight loss, and trusted education, all in one beautiful space.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <Link to={cta} data-testid="hero-join-btn" className="inline-flex items-center justify-center rounded-full bg-wine px-8 py-3.5 text-sm font-medium text-white transition-all hover:bg-wine-dark hover:shadow-md">
                Become a Member — $29/mo
              </Link>
              <Link to="/login" data-testid="hero-signin-btn" className="inline-flex items-center justify-center rounded-full border border-wine px-8 py-3.5 text-sm font-medium text-wine transition-all hover:bg-wine/5">
                I'm already a member
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="bg-white border-y border-taupe/60 py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="max-w-2xl">
            <span className="text-xs font-medium uppercase tracking-[0.2em] text-[#9B7E8D]">Your daily rhythm</span>
            <h2 className="mt-3 font-serif text-4xl sm:text-5xl tracking-tight text-[#3A1230]">A ritual that meets you each morning.</h2>
          </div>
          <div className="mt-14 grid md:grid-cols-3 gap-8">
            {features.map((f) => {
              const Icon = f.icon;
              return (
                <div key={f.title} className="bg-cream rounded-3xl p-8 border border-taupe">
                  <div className="h-12 w-12 rounded-2xl bg-wine/10 flex items-center justify-center mb-5">
                    <Icon className="h-6 w-6 text-wine" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-serif text-2xl text-[#3A1230]">{f.title}</h3>
                  <p className="mt-3 text-base text-[#6E5862] leading-relaxed">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 sm:py-28">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 text-center">
          <span className="text-xs font-medium uppercase tracking-[0.2em] text-[#9B7E8D]">Membership</span>
          <h2 className="mt-3 font-serif text-4xl sm:text-5xl tracking-tight text-wine">Simple pricing. Everything included.</h2>
          <div className="mt-12 bg-white rounded-[2rem] border border-taupe shadow-[0_20px_40px_rgba(58,18,48,0.08)] p-8 sm:p-12 text-left">
            <div className="grid grid-cols-2 gap-4">
              <div className="rounded-2xl border border-taupe p-5">
                <p className="text-xs uppercase tracking-[0.15em] text-[#9B7E8D]">Monthly</p>
                <p className="mt-1 font-serif text-5xl text-wine">$29<span className="text-lg text-[#9B7E8D]">/mo</span></p>
              </div>
              <div className="relative rounded-2xl border-2 border-wine bg-wine/5 p-5">
                <span className="absolute -top-2.5 right-4 rounded-full bg-gold px-2.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-white">Save $249</span>
                <p className="text-xs uppercase tracking-[0.15em] text-[#9B7E8D]">Annual</p>
                <p className="mt-1 font-serif text-5xl text-wine">$99<span className="text-lg text-[#9B7E8D]">/yr</span></p>
              </div>
            </div>
            <p className="mt-4 font-serif text-2xl text-[#3A1230]">BloomHer Member</p>
            <ul className="mt-6 space-y-4">
              {benefits.map((b) => (
                <li key={b} className="flex items-start gap-3">
                  <span className="mt-0.5 h-5 w-5 rounded-full bg-wine/10 flex items-center justify-center flex-shrink-0">
                    <Check className="h-3 w-3 text-wine" strokeWidth={2.5} />
                  </span>
                  <span className="text-base text-[#6E5862]">{b}</span>
                </li>
              ))}
            </ul>
            <Link to={cta} data-testid="pricing-join-btn" className="mt-10 inline-flex w-full items-center justify-center rounded-full bg-wine px-8 py-4 text-sm font-medium text-white transition-all hover:bg-wine-dark hover:shadow-md">
              Start Your Ritual
            </Link>
            <p className="mt-4 text-center text-xs text-[#9B7E8D]">
              Clinical care, diagnoses & prescriptions are handled by our partner platform.
            </p>
          </div>
        </div>
      </section>

      <footer className="border-t border-taupe/60 py-10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <BloomHerLogo size="text-xl" />
          </div>
          <p className="text-xs text-[#9B7E8D] text-center max-w-md">
            BloomHer is a wellness & education membership. It is not a substitute for medical advice, diagnosis, or treatment.
          </p>
        </div>
      </footer>
    </div>
  );
}
